import { sortBy, uniq } from 'ramda';
import { RheologyDataDto } from 'providers/api';
import { Model } from '../hooks/useModelState';
import { RouteGenerationError } from '../MineModel/Route/errors';
import PCRoute from '../MineModel/Route/PCRoute';
import PCStope from '../MineModel/Stopes/PCStope';
import { generateAllRoutes, liveRouteFor, routeOptsForProject } from '../MineModel/Route/utils';
import { spoolDisplayName } from '../MineModel/Spools/utils';
import { evaluateStope } from './evaluateStope';
import { buildLadders } from './pipeLadders';
import { describeSegments, firstActiveSpoolLookup } from './routeSegments';
import { runAutoPipeAssigner, yieldToBrowser } from './runAutoPipeAssigner';
import {
  AssignerProgress,
  AssignerRun,
  Assignment,
  AutoPipeAssignerResult,
  AutoPipeAssignerSettings,
  FailureReason,
  Ladders,
  OwnerInfo,
  PipeTotal,
  PipeUsage,
  SegmentInfo,
  SpoolChange,
  StopeOutcome,
  StopeRow
} from './types';

export interface AutoAssignPipesInput {
  model: Model;
  stopes: PCStope[];
  rheology: RheologyDataDto;
  settings: AutoPipeAssignerSettings;
  usage: Record<string, PipeUsage>;
  signal?: AbortSignal;
  onProgress?: (progress: AssignerProgress) => void;
}

interface PreparedStope {
  stope: PCStope;
  route: PCRoute;
  segments: SegmentInfo[];
}

interface UnroutedStope {
  stope: PCStope;
  reason: FailureReason;
}

type StopeSetup = PreparedStope | UnroutedStope;

const isPrepared = (setup: StopeSetup): setup is PreparedStope => 'route' in setup;

const stopeName = (stope: PCStope): string => stope.data.stopeName || stope.data.stopeId;

// The route every new plan uses: the shortest all-active one, as Auto Recipe and Auto Pour pick it.
const liveRouteForStope = (model: Model, stope: PCStope): { route: PCRoute } | { reason: FailureReason } => {
  try {
    const { routeObjects } = generateAllRoutes(
      model.activeBackfillPlant,
      model.spools,
      stope,
      Object.values(model.pipes),
      routeOptsForProject(model.project),
      model.nodes,
      model.pipes,
    );
    const route = liveRouteFor(routeObjects);
    return route ? { route } : { reason: 'noRoute' };
  } catch (error) {
    if (!(error instanceof RouteGenerationError)) return { reason: 'noRoute' };
    return { reason: error.reason === 'missing-pipe' ? 'missingStopePipe' : 'noStopePipeArrays' };
  }
};

const prepareStopes = (
  { model, stopes, signal, onProgress }: AutoAssignPipesInput,
): Promise<StopeSetup[] | undefined> => {
  const lookup = firstActiveSpoolLookup(model.spools);
  return stopes.reduce(
    (previous: Promise<StopeSetup[] | undefined>, stope, stopeIndex) => previous.then(async (prepared) => {
      if (!prepared) return undefined;
      onProgress?.({
        stage: 'routes', stopeIndex, stopeCount: stopes.length, pass: 0,
      });
      await yieldToBrowser();
      if (signal?.aborted) return undefined;
      const found = liveRouteForStope(model, stope);
      return [...prepared, 'route' in found ? { stope, route: found.route, segments: describeSegments(found.route, lookup) } : { stope, reason: found.reason }];
    }),
    Promise.resolve([]),
  );
};

const ownerIdsOf = ({ segments }: PreparedStope): string[] => uniq(segments.flatMap(({ ownerSpoolId }) => (ownerSpoolId ? [ownerSpoolId] : [])));

const ownersOf = (model: Model, prepared: PreparedStope[]): Record<string, OwnerInfo> => prepared
  .flatMap(({ segments }) => segments)
  .reduce<Record<string, OwnerInfo>>((acc, { ownerSpoolId, kind }) => (ownerSpoolId && kind !== 'discharge' && model.spools[ownerSpoolId]
    ? { ...acc, [ownerSpoolId]: { spoolClass: kind, livePipeId: model.spools[ownerSpoolId].pipe.pipeId } }
    : acc), {});

// Only spools on the selected stopes' routes move; a class with no usable pipe leaves its spools where they are.
const lowestPipes = (owners: Record<string, OwnerInfo>, ladders: Ladders): Assignment => new Map(
  Object.entries(owners).flatMap(([ownerId, { spoolClass, livePipeId }]): Array<[string, string]> => {
    const [lowest] = ladders[spoolClass];
    return lowest && lowest.pipeId !== livePipeId ? [[ownerId, lowest.pipeId]] : [];
  }),
);

// A spool that climbed from the lowest pipe back to its own has not changed.
const withoutLivePipes = (run: AssignerRun, owners: Record<string, OwnerInfo>): AssignerRun => ({
  ...run,
  assignment: new Map(Array.from(run.assignment.entries()).filter(([ownerId, pipeId]) => owners[ownerId]?.livePipeId !== pipeId)),
});

const onRoute = (spoolId: string) => ({ segments }: PreparedStope) => segments.some((segment) => segment.ownerSpoolId === spoolId);

const peakPressureOf = (outcomes: StopeOutcome[], spoolId: string): number | undefined => outcomes
  .map((outcome) => outcome.evaluation?.peakPressureByOwner[spoolId])
  .reduce<number | undefined>((peak, envelope) => (envelope !== undefined && (peak === undefined || envelope > peak) ? envelope : peak), undefined);

const buildChanges = (model: Model, prepared: PreparedStope[], owners: Record<string, OwnerInfo>, run: AssignerRun): SpoolChange[] => sortBy(
  (change: SpoolChange) => change.label,
  Array.from(run.assignment.entries()).map(([spoolId, toPipeId]) => {
    const spool = model.spools[spoolId];
    const toPipe = model.pipes[toPipeId];
    return {
      spoolId,
      label: spoolDisplayName(spool),
      spoolClass: owners[spoolId].spoolClass,
      length: spool.length(),
      fromPipeId: spool.pipe.pipeId,
      fromPipeName: spool.pipe.name,
      toPipeId,
      toPipeName: toPipe.name,
      toRatingKpa: toPipe.pressureRating,
      peakPressureKpa: peakPressureOf(Object.values(run.outcomes), spoolId),
      stopeNames: prepared.filter(onRoute(spoolId)).map(({ stope }) => stopeName(stope)),
    };
  }),
);

// Metres of each new pipe, for procurement.
const buildTotals = (changes: SpoolChange[]): PipeTotal[] => sortBy(
  (total: PipeTotal) => total.pipeName,
  Object.values(changes.reduce<Record<string, PipeTotal>>((acc, { toPipeId, toPipeName, length }) => ({
    ...acc,
    [toPipeId]: { pipeId: toPipeId, pipeName: toPipeName, metres: (acc[toPipeId]?.metres ?? 0) + length },
  }), {})),
);

const stopeRow = (model: Model, setup: StopeSetup, run: AssignerRun): StopeRow => {
  const { stope } = setup;
  const base = { stopeId: stope.data.stopeId, stopeName: stopeName(stope) };
  if (!isPrepared(setup)) return { ...base, status: 'failed', reason: setup.reason };
  const outcome = run.outcomes[stope.data.stopeId];

  const metrics = outcome.evaluation && {
    concentrationByMass: outcome.evaluation.concentrationByMass,
    pumpPressureKpa: outcome.evaluation.pumpPressureKpa,
    peakShareOfRating: outcome.evaluation.peakShareOfRating,
  };
  if (outcome.status === 'passed') {
    const changed = setup.segments.some(({ ownerSpoolId }) => !!ownerSpoolId && run.assignment.has(ownerSpoolId));
    return { ...base, ...metrics, status: changed ? 'passedWithChanges' : 'passedUnchanged' };
  }
  const blockingLabels = (outcome.blockingSpoolIds ?? []).flatMap((spoolId) => (model.spools[spoolId] ? [spoolDisplayName(model.spools[spoolId])] : []));
  return {
    ...base,
    ...metrics,
    status: 'failed',
    reason: outcome.reason,
    blockingSpoolLabel: blockingLabels.length > 0 ? blockingLabels.join(', ') : undefined,
    blockingClass: outcome.blockingClass,
  };
};

// The solver prices friction onto the model's shared pipes; the live HGL must find them as it left them.
const snapshotPipeFlow = (model: Model) => {
  const saved = Object.values(model.pipes).map((pipe) => ({ pipe, friction: pipe.friction, velocity: pipe.velocity }));
  return () => saved.forEach(({ pipe, friction, velocity }) => {
    Object.assign(pipe, { friction, velocity });
  });
};

/**
 * Plans pipe changes for the selected stopes without writing anything. Resolves undefined when cancelled.
 */
export const autoAssignPipes = async (input: AutoAssignPipesInput): Promise<AutoPipeAssignerResult | undefined> => {
  const {
    model, rheology, settings, usage, signal, onProgress,
  } = input;
  const restorePipeFlow = snapshotPipeFlow(model);
  try {
    const setups = await prepareStopes(input);
    if (!setups) return undefined;
    const prepared = setups.filter(isPrepared);
    const byStopeId = new Map(prepared.map((stope) => [stope.stope.data.stopeId, stope]));
    const owners = ownersOf(model, prepared);
    const context = { model, rheology, settings };
    const ladders = buildLadders(Object.values(model.pipes), usage);

    const planned = await runAutoPipeAssigner({
      stopeIds: prepared.map(({ stope }) => stope.data.stopeId),
      ownersByStope: Object.fromEntries(prepared.map((stope) => [stope.stope.data.stopeId, ownerIdsOf(stope)])),
      owners,
      pipes: model.pipes,
      ladders,
      evaluate: (stopeId, assignment) => {
        const { route, segments } = byStopeId.get(stopeId) as PreparedStope;
        return evaluateStope(context, route, segments, assignment);
      },
      initialAssignment: settings.startFrom === 'lowest' ? lowestPipes(owners, ladders) : undefined,
      signal,
      onProgress,
    });
    if (!planned) return undefined;
    const run = withoutLivePipes(planned, owners);

    const changes = buildChanges(model, prepared, owners, run);
    return {
      stopes: setups.map((setup) => stopeRow(model, setup, run)),
      changes,
      totals: buildTotals(changes),
      converged: run.converged,
    };
  } finally {
    restorePipeFlow();
  }
};

// Someone edited a pipe, or undo ran, between the run and Apply.
export const isResultStale = (model: Model, changes: SpoolChange[]): boolean => changes.some(
  ({ spoolId, fromPipeId }) => model.spools[spoolId]?.pipe.pipeId !== fromPipeId,
);
